import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "@/context/LanguageContext";

const routes = [
    { to: "/", key: "home" },
    { to: "/gioi-thieu", key: "about" },
    { to: "/dich-vu", key: "services" },
    { to: "/du-an", key: "projects" },
    { to: "/blog", key: "blog" },
    { to: "/lien-he", key: "contact" },
];

function LangToggle({ lang, setLang, t }) {
    return (
        <div
            className="flex items-center rounded-full border border-slate-700/80 bg-ink2/80 p-0.5"
            data-testid="lang-toggle"
        >
            {["vi", "en"].map((l) => (
                <button
                    key={l}
                    type="button"
                    data-testid={`lang-toggle-${l}`}
                    onClick={() => setLang(l)}
                    className={`rounded-full px-3 py-1 font-mono text-[11px] font-medium tracking-widest transition-colors duration-300 ${
                        lang === l ? "bg-neon text-ink" : "text-slate-400 hover:text-white"
                    }`}
                >
                    {l === "vi" ? t.common.langVi : t.common.langEn}
                </button>
            ))}
        </div>
    );
}

export default function Navbar() {
    const { lang, setLang, t } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled ? "glass border-b border-slate-800/70" : "border-b border-transparent"
            }`}
            data-testid="navbar"
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
                <Link to="/" data-testid="nav-logo" aria-label="MAXTH Solutions">
                    <Logo />
                </Link>

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
                    {routes.map((r) => (
                        <NavLink
                            key={r.key}
                            to={r.to}
                            end={r.to === "/"}
                            data-testid={`nav-link-${r.key}`}
                            className={({ isActive }) =>
                                `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                                    isActive ? "text-neon" : "text-slate-400 hover:text-white"
                                }`
                            }
                        >
                            {t.nav[r.key]}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 lg:flex">
                    <LangToggle lang={lang} setLang={setLang} t={t} />
                    <Link
                        to="/lien-he"
                        data-testid="nav-cta-button"
                        className="group flex items-center gap-1.5 rounded-full bg-neon px-5 py-2.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-white"
                    >
                        {t.nav.cta}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>

                <div className="flex items-center gap-3 lg:hidden">
                    <LangToggle lang={lang} setLang={setLang} t={t} />
                    <button
                        type="button"
                        data-testid="mobile-menu-button"
                        aria-label="Menu"
                        onClick={() => setOpen(!open)}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 text-slate-200"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="glass overflow-hidden border-b border-slate-800 lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="space-y-1 px-5 py-6">
                            {routes.map((r, i) => (
                                <motion.div
                                    key={r.key}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <NavLink
                                        to={r.to}
                                        end={r.to === "/"}
                                        data-testid={`mobile-nav-link-${r.key}`}
                                        className={({ isActive }) =>
                                            `block rounded-xl px-4 py-3 font-display text-2xl font-bold tracking-tight ${
                                                isActive ? "text-neon" : "text-slate-200"
                                            }`
                                        }
                                    >
                                        {t.nav[r.key]}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <div className="pt-4">
                                <Link
                                    to="/lien-he"
                                    data-testid="mobile-nav-cta-button"
                                    className="flex items-center justify-center gap-2 rounded-full bg-neon px-5 py-3.5 text-sm font-semibold text-ink"
                                >
                                    {t.nav.cta}
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
